import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TextField, InputAdornment, ToggleButtonGroup, ToggleButton, Pagination, Stack, Grid, Typography, Box } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import handleService from "../../services/apiService.ts";
import cirurgiaService from "../../services/cirurgiaService.ts";
import { formatBrazilPhone } from "../../utils/phone.util.ts";
import { formatterCPF } from "../../utils/cpf.util.ts";
import { maskCep } from "../../utils/address.util.ts";
import EmptyState from "../../ui/EmptyState.tsx";
import "../../styles/design-system.css";

interface Instrumentador {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  cpf: string;
  endereco?: {
    cep?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    rua?: string;
    numero?: string;
  };
}

interface CirurgiaResumo {
  id: string;
  data: string;
  medico: string;
  tipoCirurgia: string;
  hospital: string;
  status: string;
}

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

// "Ana Beatriz Ramos" -> "AB" (primeira letra do primeiro e do último nome).
const getIniciais = (nome: string) => {
  const partes = (nome || "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  const primeira = partes[0][0] || "";
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : "";
  return (primeira + ultima).toUpperCase();
};

// Cirurgia sem horário definido fica salva com 00:00 no banco — exibir isso
// como se fosse "meia-noite" confundia mais do que ajudava, daí o tratamento
// como "sem horário" explícito. Ano sempre exibido (ex: "AGO/2030") pra não
// deixar ambíguo se o procedimento é passado ou futuro só olhando dia/mês.
const formatDataHora = (iso: string) => {
  const d = new Date(iso);
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = MESES[d.getMonth()];
  const ano = d.getFullYear();
  const hora = d.getHours();
  const min = d.getMinutes();
  const semHorario = hora === 0 && min === 0;
  const horario = semHorario ? null : `${String(hora).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
  return { dia, mes, ano, horario };
};

const ordenarPorData = (lista: CirurgiaResumo[]) =>
  [...lista].sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime());

const ITENS_POR_PAGINA = 8;

// Sem ThemeProvider do MUI, cores de estado (borda/label focado, texto
// desabilitado, seleção) sempre caem no tema claro padrão — CSS vars do
// design system em vez de tokens de tema, mesmo padrão do resto do app.
const searchFieldSx = {
  minWidth: 220,
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    background: "var(--surface)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
  },
  "& .MuiInputBase-input::placeholder": { color: "var(--ink-faint)", opacity: 1 },
};

// Mesma receita do toggle Diário/Mensal do Dashboard — reaproveitada aqui
// pra manter o mesmo padrão visual de filtro compacto em toda a base.
const statusToggleSx = {
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: "999px",
  padding: "3px",
  "& .MuiToggleButton-root": {
    color: "var(--ink-soft)",
    border: "none",
    borderRadius: "999px !important",
    textTransform: "none",
    fontWeight: 700,
    fontSize: 12.6,
    padding: "5px 14px",
  },
  "& .MuiToggleButton-root:hover": { backgroundColor: "var(--surface-3)", color: "var(--ink)" },
  "& .MuiToggleButton-root.Mui-selected": { color: "var(--ink)", backgroundColor: "var(--surface)", boxShadow: "var(--shadow-sm)" },
  "& .MuiToggleButton-root.Mui-selected:hover": { backgroundColor: "var(--surface)", color: "var(--ink)" },
};

const paginationSx = {
  "& .MuiPaginationItem-root": { color: "var(--ink-soft)" },
  "& .MuiPaginationItem-root:hover": { backgroundColor: "var(--surface-2)" },
  "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "var(--blue)", color: "#fff" },
  "& .MuiPaginationItem-root.Mui-selected:hover": { backgroundColor: "var(--blue-hover)" },
};

type StatusFiltro = "TODAS" | "AGENDADA" | "REALIZADA";

const STATUS_LABELS: Record<string, string> = {
  AGENDADA: "Agendada",
  REALIZADA: "Realizada",
  SEPARADO: "Separado",
  CANCELADA: "Cancelada",
  ABERTO: "Aberto",
};

const ProcedimentoItem = ({ cirurgia, mostrarStatus }: { cirurgia: CirurgiaResumo; mostrarStatus?: boolean }) => {
  const { dia, mes, ano, horario } = formatDataHora(cirurgia.data);
  return (
    <div className="ds-procedimento-item">
      <div className="ds-procedimento-date">
        <span className="dia">{dia}</span>
        <span className="mes">{mes}/{ano}</span>
      </div>
      <div className="ds-procedimento-info">
        <strong>{cirurgia.medico}</strong>
        <span>{cirurgia.tipoCirurgia} — {cirurgia.hospital}</span>
      </div>
      <div className="ds-procedimento-right">
        {mostrarStatus && <span className="ds-procedimento-status">{STATUS_LABELS[cirurgia.status] || cirurgia.status}</span>}
        <span className={`ds-procedimento-horario ${horario ? "" : "sem-horario"}`}>{horario ? horario : "Sem horário"}</span>
      </div>
    </div>
  );
};

// Monta o endereço em até 3 linhas legíveis em vez de campos espalhados:
// "Rua X, 123" / "Bairro, Cidade - UF" / "CEP: 00000-000".
const formatarEndereco = (endereco?: Instrumentador["endereco"]) => {
  if (!endereco) return [];
  const linhas: string[] = [];

  if (endereco.rua) linhas.push(endereco.rua + (endereco.numero ? `, ${endereco.numero}` : ""));

  const bairroCidadeUf = [
    endereco.bairro,
    [endereco.cidade, endereco.uf].filter(Boolean).join(" - "),
  ].filter(Boolean).join(", ");
  if (bairroCidadeUf) linhas.push(bairroCidadeUf);

  if (endereco.cep) linhas.push(`CEP: ${maskCep(endereco.cep)}`);

  return linhas;
};

// Linha "ícone + texto" da ficha de contato — sem rótulo em caixa alta em
// cima, o ícone já dá o contexto (padrão comum de sidebar de perfil em
// SaaS: GitHub, Vercel etc.). Cor do ícone sutil via CSS var, não
// "text.secondary" — sem ThemeProvider esse token não acompanha o dark mode.
const LinhaFicha = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <Box sx={{ color: "var(--ink-faint)", display: "flex", mt: "1px" }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Stack>
);

const PerfilInstrumentador = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [instrumentador, setInstrumentador] = useState<Instrumentador | null>(null);
  const [totalCirurgias, setTotalCirurgias] = useState<number | null>(null);
  const [proximas, setProximas] = useState<CirurgiaResumo[]>([]);
  const [agenda, setAgenda] = useState<CirurgiaResumo[] | null>(null);
  const [aba, setAba] = useState<"visao-geral" | "agenda">("visao-geral");
  const [isLoading, setIsLoading] = useState(true);

  // Filtro/paginação são client-side por enquanto (dataset já buscado
  // inteiro) — quando o backend ganhar busca por médico e paginação real
  // nessa rota, troca-se só o fetch, a UI já fica pronta.
  const [agendaBusca, setAgendaBusca] = useState("");
  const [agendaStatus, setAgendaStatus] = useState<StatusFiltro>("TODAS");
  const [agendaPagina, setAgendaPagina] = useState(1);

  useEffect(() => {
    const carregar = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        // Não existe GET /instrumentador/{id} no backend — busca a lista
        // inteira (já usada na listagem, dataset pequeno) e filtra pelo id.
        const respInstrumentadores = await handleService({ endpoint: "/instrumentador" });
        const lista: Instrumentador[] = Array.isArray(respInstrumentadores) ? respInstrumentadores : respInstrumentadores?.data || [];
        const encontrado = lista.find((i) => String(i.id) === id) || null;
        setInstrumentador(encontrado);

        if (encontrado) {
          const [total, prox] = await Promise.all([
            cirurgiaService.contarPorInstrumentador(id),
            cirurgiaService.listarProximasPorInstrumentador(id, 5),
          ]);
          setTotalCirurgias(total);
          // Reordena no client independente do que o backend devolver — a
          // exibição depende disso pra não parecer fora de ordem.
          setProximas(ordenarPorData(prox));
        }
      } finally {
        setIsLoading(false);
      }
    };
    carregar();
  }, [id]);

  useEffect(() => {
    if (aba === "agenda" && id && agenda === null) {
      cirurgiaService.listarTodasPorInstrumentador(id, 200).then((lista) => setAgenda(ordenarPorData(lista).reverse()));
    }
  }, [aba, id, agenda]);

  const agendaFiltrada = useMemo(() => {
    if (!agenda) return [];
    const termo = agendaBusca.trim().toLowerCase();
    return agenda.filter((c) => {
      const bateBusca = !termo || c.medico?.toLowerCase().includes(termo);
      const bateStatus = agendaStatus === "TODAS" || c.status === agendaStatus;
      return bateBusca && bateStatus;
    });
  }, [agenda, agendaBusca, agendaStatus]);

  // Volta pra primeira página sempre que o filtro muda o resultado —
  // senão dá pra "sumir" numa página vazia depois de filtrar.
  useEffect(() => {
    setAgendaPagina(1);
  }, [agendaBusca, agendaStatus]);

  const totalPaginasAgenda = Math.max(1, Math.ceil(agendaFiltrada.length / ITENS_POR_PAGINA));
  const agendaPaginada = agendaFiltrada.slice((agendaPagina - 1) * ITENS_POR_PAGINA, agendaPagina * ITENS_POR_PAGINA);

  return (
    <div className="ds">
      <div className="ds-breadcrumb">
        <span>Cadastros</span>
        <span className="sep">/</span>
        <span className="ds-breadcrumb-link" onClick={() => navigate("/instrumentador/listagem")}>Instrumentadores</span>
        <span className="sep">/</span>
        <span className="current">Perfil</span>
      </div>
      <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm" style={{ marginBottom: 16, paddingLeft: 0 }} onClick={() => navigate("/instrumentador/listagem")}>
        <ArrowBackIcon fontSize="small" /> Voltar para Instrumentadores
      </button>

      {isLoading ? (
        <div className="ds-card" style={{ padding: 40, textAlign: "center", color: "var(--ink-faint)" }}>Carregando...</div>
      ) : !instrumentador ? (
        <EmptyState title="Instrumentador não encontrado" description="Ele pode ter sido removido." actionLabel="Voltar para Instrumentadores" onAction={() => navigate("/instrumentador/listagem")} />
      ) : (
        <>
          <div className="ds-card ds-profile-head">
            <div className="ds-profile-avatar">{getIniciais(instrumentador.nome)}</div>
            <div className="ds-profile-info">
              <h2>{instrumentador.nome}</h2>
              <div className="ds-profile-meta">
                <span><MailOutlineIcon fontSize="inherit" /> {instrumentador.email || "—"}</span>
                <span><PhoneOutlinedIcon fontSize="inherit" /> {formatBrazilPhone(instrumentador.telefone) || instrumentador.telefone || "—"}</span>
              </div>
            </div>
            <div className="ds-profile-stats">
              <div>
                <div className="n">{totalCirurgias ?? "—"}</div>
                <div className="l">Cirurgias</div>
              </div>
            </div>
          </div>

          <div className="ds-tabs">
            <button type="button" className={`ds-tab ${aba === "visao-geral" ? "active" : ""}`} onClick={() => setAba("visao-geral")}>Visão geral</button>
            <button type="button" className={`ds-tab ${aba === "agenda" ? "active" : ""}`} onClick={() => setAba("agenda")}>Agenda</button>
          </div>

          {aba === "visao-geral" && (
            // alignItems="flex-start" de propósito: o Grid do MUI estica os
            // itens de uma linha pra mesma altura por padrão (align-items
            // stretch do flexbox) — sem isso, o card de Dados Cadastrais
            // (bem mais curto) ficava esticado até a altura do card de
            // Próximos procedimentos, criando um vão vazio enorme embaixo.
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <div className="ds-card ds-chart-card">
                  <div className="ds-chart-card-head"><h3>Dados Cadastrais</h3></div>
                  <Stack spacing={3} sx={{ pt: 0.5 }}>
                    <LinhaFicha icon={<BadgeOutlinedIcon fontSize="small" />}>
                      <Typography variant="body2" sx={{ color: "var(--ink)", fontWeight: 600 }}>
                        {instrumentador.cpf ? formatterCPF(instrumentador.cpf) : "CPF não informado"}
                      </Typography>
                    </LinhaFicha>

                    <LinhaFicha icon={<MailOutlineIcon fontSize="small" />}>
                      <Typography variant="body2" sx={{ color: "var(--ink)", fontWeight: 600, wordBreak: "break-word" }}>{instrumentador.email || "—"}</Typography>
                    </LinhaFicha>

                    <LinhaFicha icon={<PhoneOutlinedIcon fontSize="small" />}>
                      <Typography variant="body2" sx={{ color: "var(--ink)", fontWeight: 600 }}>{formatBrazilPhone(instrumentador.telefone) || instrumentador.telefone || "—"}</Typography>
                    </LinhaFicha>

                    <LinhaFicha icon={<LocationOnOutlinedIcon fontSize="small" />}>
                      {(() => {
                        const linhas = formatarEndereco(instrumentador.endereco);
                        return linhas.length === 0 ? (
                          <Typography variant="body2" sx={{ color: "var(--ink-faint)" }}>Endereço não informado</Typography>
                        ) : (
                          <Stack spacing={0.25}>
                            {linhas.map((linha, i) => (
                              <Typography key={i} variant="body2" sx={{ color: "var(--ink)", fontWeight: i === 0 ? 600 : 500 }}>
                                {linha}
                              </Typography>
                            ))}
                          </Stack>
                        );
                      })()}
                    </LinhaFicha>
                  </Stack>
                </div>
              </Grid>

              <Grid item xs={12} md={8}>
                <div className="ds-card ds-chart-card">
                  <div className="ds-chart-card-head"><h3>Próximos procedimentos</h3></div>
                  {proximas.length === 0 ? (
                    <p style={{ color: "var(--ink-faint)", fontSize: 13.2 }}>Nenhuma cirurgia agendada.</p>
                  ) : (
                    <div className="ds-procedimentos-list">
                      {proximas.map((c) => <ProcedimentoItem key={c.id} cirurgia={c} />)}
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          )}

          {aba === "agenda" && (
            <div className="ds-card ds-chart-card">
              <div className="ds-chart-card-head" style={{ flexWrap: "wrap", gap: 12 }}>
                <h3>Agenda do instrumentador</h3>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ ml: "auto" }}>
                  <TextField
                    size="small"
                    placeholder="Buscar médico..."
                    value={agendaBusca}
                    onChange={(e) => setAgendaBusca(e.target.value)}
                    sx={searchFieldSx}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "var(--ink-faint)" }} /></InputAdornment>,
                    }}
                  />
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={agendaStatus}
                    onChange={(_, valor) => valor && setAgendaStatus(valor)}
                    sx={statusToggleSx}
                  >
                    <ToggleButton value="TODAS">Todas</ToggleButton>
                    <ToggleButton value="AGENDADA">Agendadas</ToggleButton>
                    <ToggleButton value="REALIZADA">Realizadas</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </div>

              {agenda === null ? (
                <p style={{ color: "var(--ink-faint)", fontSize: 13.2 }}>Carregando...</p>
              ) : agendaFiltrada.length === 0 ? (
                <p style={{ color: "var(--ink-faint)", fontSize: 13.2 }}>
                  {agenda.length === 0 ? "Nenhuma cirurgia registrada para este instrumentador." : "Nenhum resultado para esse filtro."}
                </p>
              ) : (
                <>
                  <div className="ds-procedimentos-list">
                    {agendaPaginada.map((c) => <ProcedimentoItem key={c.id} cirurgia={c} mostrarStatus />)}
                  </div>
                  {totalPaginasAgenda > 1 && (
                    <Stack alignItems="center" sx={{ mt: 2.5 }}>
                      <Pagination
                        count={totalPaginasAgenda}
                        page={agendaPagina}
                        onChange={(_, valor) => setAgendaPagina(valor)}
                        color="primary"
                        shape="rounded"
                        sx={paginationSx}
                      />
                    </Stack>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PerfilInstrumentador;
